import { logAmplitudeEvent } from 'amplitude/amplitude'
import { orgSelectors } from 'ducks/orgs'
import { useNotify } from 'ra-core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import restClient from 'restClient'

export interface RecommendedProductsParams {
  orgId?: number
  types?: string[]
  distributors?: string[]
  inStock?: boolean
  limit?: number
  timeframeDays?: number
}

export interface RecommendedProduct {
  id: number
  code: string
  title: string
  manufacturer: string
  rating: number
  type: string
  recommendation_reason: 'designed' | 'purchased' | 'popular' | 'designed_and_purchased'
  last_used_date: string | null
  usage_count: number
  ordering_v2: any
  is_pinned: boolean
  boost_score: number
}

export interface RecommendedProductsResponse {
  recommended_products: RecommendedProduct[]
  metadata: {
    org_id: number
    role_id: number | null
    source: 'designs' | 'purchases'
    timeframe_days: number
    total_designed_components: number
    total_purchased_components: number
    fallback_used: boolean
    cache_hit: boolean
  }
}

type RecommendationSource = 'designs' | 'purchases'

type SourceHookConfig = {
  source: RecommendationSource
  fetchEventName: 'shop_design_recommendations_fetched' | 'shop_purchase_recommendations_fetched'
  section: 'from_your_designs' | 'from_your_past_purchases'
  fetchErrorNotification: string
  fetchErrorLogPrefix: string
}

const restClientInstance = restClient(window.API_ROOT + '/api')
const DISTRIBUTOR_FILTER_KEY_REGEX = /^[A-Za-z0-9_-]+$/

const sanitizeDistributors = (distributors?: string[]) =>
  (distributors || [])
    .map((dist) => dist?.trim())
    .filter((dist): dist is string => Boolean(dist))
    .filter((dist) => DISTRIBUTOR_FILTER_KEY_REGEX.test(dist))

const buildRecommendationQueryString = (params: RecommendedProductsParams, sanitizedDistributors: string[]) => {
  const queryParts: string[] = []
  if (params.types && params.types.length > 0) {
    params.types.forEach((type) => queryParts.push(`types=${encodeURIComponent(type)}`))
  }
  if (sanitizedDistributors.length > 0) {
    sanitizedDistributors.forEach((dist) => queryParts.push(`distributors=${encodeURIComponent(dist)}`))
  }
  if (params.inStock !== undefined) {
    queryParts.push(`in_stock=${params.inStock}`)
  }
  if (params.limit) {
    queryParts.push(`limit=${params.limit}`)
  }
  if (params.timeframeDays) {
    queryParts.push(`timeframe_days=${params.timeframeDays}`)
  }
  return queryParts.length > 0 ? `?${queryParts.join('&')}` : ''
}

export const useRecommendedProductsBySource = (params: RecommendedProductsParams, config: SourceHookConfig) => {
  const { source, fetchEventName, section, fetchErrorNotification, fetchErrorLogPrefix } = config
  const [data, setData] = useState<RecommendedProductsResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const orgId = useSelector(orgSelectors.getOrg)?.id || params.orgId || 0
  const notify = useNotify()

  const typesKey = useMemo(() => params.types?.slice().sort().join(',') || '', [params.types])
  const distributorsKey = useMemo(() => params.distributors?.slice().sort().join(',') || '', [params.distributors])

  const fetchRecommendations = useCallback(() => {
    if (!orgId) {
      return
    }

    setIsLoading(true)
    setError(null)

    const sanitizedDistributors = sanitizeDistributors(params.distributors)
    const queryString = buildRecommendationQueryString(params, sanitizedDistributors)
    const sourceParam = `source=${encodeURIComponent(source)}`
    const separator = queryString ? '&' : '?'
    const url = `orgs/${orgId}/recommended_products/${queryString}${separator}${sourceParam}`

    restClientInstance('CUSTOM_GET', 'custom', { url })
      .then((response) => {
        if (!response?.data) {
          return
        }

        setData(response.data)
        logAmplitudeEvent(fetchEventName, {
          source: 'home_page',
          section,
          recommendation_source: source,
          result_count: response.data.recommended_products?.length || 0,
          fallback_used: response.data.metadata?.fallback_used || false,
          cache_hit: response.data.metadata?.cache_hit || false,
          types: params.types || [],
          distributors: sanitizedDistributors,
        })
      })
      .catch((err: any) => {
        setError(err)
        notify(fetchErrorNotification, 'warning')
        console.error(`${fetchErrorLogPrefix}:`, err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [
    orgId,
    typesKey,
    distributorsKey,
    params.distributors,
    params.inStock,
    params.limit,
    params.timeframeDays,
    params.types,
    fetchErrorLogPrefix,
    fetchErrorNotification,
    fetchEventName,
    notify,
    section,
    source,
  ])

  useEffect(() => {
    fetchRecommendations()
  }, [fetchRecommendations])

  return { data, isLoading, error, refetch: fetchRecommendations }
}
